'use client'

/**
 * Rejestracja Web Push. Montowane raz w layoucie /installer.
 *
 * Świadomie NIE pytamy o zgodę automatycznie przy wejściu — przeglądarki karzą
 * za to trwałą blokadą, a użytkownik i tak klika „Zablokuj" na dzień dobry.
 * Zamiast tego pokazujemy dyskretny pasek, który tłumaczy PO CO to jest, i
 * dopiero jego kliknięcie wywołuje systemowy prompt.
 *
 * Gdy zgoda już jest — subskrybujemy po cichu przy każdym wejściu, bo
 * subskrypcja potrafi wygasnąć i trzeba ją odświeżyć.
 */
import { useCallback, useEffect, useState } from 'react'
import { Bell, X } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'

const DISMISS_KEY = 'heizos-push-dismissed'

/** VAPID public key (base64url) → ArrayBuffer wymagany przez PushManager.
 *  Zwracamy ArrayBuffer, a nie Uint8Array: w nowszym lib.dom `Uint8Array` jest
 *  generyczny po ArrayBufferLike i nie wchodzi w `BufferSource`. */
function urlBase64ToBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const buf = new ArrayBuffer(raw.length)
  const view = new Uint8Array(buf)
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i)
  return buf
}

export default function PushSetup() {
  const token = useAuthStore((s) => s.token)
  const [showPrompt, setShowPrompt] = useState(false)
  const [busy, setBusy] = useState(false)

  const subscribe = useCallback(async (): Promise<boolean> => {
    const vapid = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY
    if (!vapid || !token) return false
    try {
      const reg = await navigator.serviceWorker.register('/sw.js')
      await navigator.serviceWorker.ready
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBuffer(vapid),
      })
      const r = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(sub.toJSON()),
      })
      return r.ok
    } catch (e) {
      console.error('[push] subscribe failed', e)
      return false
    }
  }, [token])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    if (!token) return

    if (Notification.permission === 'granted') {
      void subscribe()                                    // odśwież po cichu
    } else if (Notification.permission === 'default') {
      if (localStorage.getItem(DISMISS_KEY) !== '1') setShowPrompt(true)
    }
    // 'denied' — nic nie robimy, przeglądarka i tak nie pokaże promptu
  }, [token, subscribe])

  const enable = async () => {
    setBusy(true)
    try {
      const perm = await Notification.requestPermission()
      if (perm === 'granted') await subscribe()
    } finally {
      setBusy(false)
      setShowPrompt(false)
    }
  }

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1')
    setShowPrompt(false)
  }

  if (!showPrompt) return null

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-sm rounded-xl border dark:border-installer-border border-gray-2 dark:bg-installer-card bg-white shadow-lg p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#FFD400]/15">
          <Bell className="h-4 w-4 text-[#FFD400]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold dark:text-white text-dark">Powiadomienia na pulpicie</p>
          <p className="mt-0.5 text-xs text-gray-1">
            Monter zada pytanie z budowy — dowiesz się od razu, nawet z zamkniętą kartą 43OS.
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              onClick={enable}
              disabled={busy}
              className="rounded-lg bg-[#FFD400] px-3 py-1.5 text-xs font-semibold text-[#111111] hover:bg-[#FFE74A] disabled:opacity-50"
            >
              {busy ? 'Włączam…' : 'Włącz'}
            </button>
            <button onClick={dismiss} className="text-xs text-gray-1 hover:dark:text-white hover:text-dark">
              Nie teraz
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="text-gray-1 hover:dark:text-white hover:text-dark" aria-label="Zamknij">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
